import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock } from "lucide-react";
import { ARTICLES } from "../data/content";
import { ArticleCardGrid } from "../components/ArticleCard";

export default function ArticleDetail() {
  const { id } = useParams();
  const article = ARTICLES.find((a) => a.id === id);
  if (!article) return <Navigate to="/writings" replace />;

  const related = ARTICLES.filter(
    (a) => a.id !== id && a.category === article.category
  )
    .concat(ARTICLES.filter((a) => a.id !== id))
    .filter((a, i, arr) => arr.findIndex((x) => x.id === a.id) === i)
    .slice(0, 3);

  return (
    <article data-testid="article-detail-page">
      <div className="relative h-[52vh] min-h-[380px] w-full overflow-hidden">
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          src={article.image}
          alt={article.title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-[var(--bg)]/40 to-[var(--ink)]/40" />
      </div>

      <div className="max-w-3xl mx-auto px-6 sm:px-10 -mt-32 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Link
            to="/writings"
            data-testid="back-to-writings"
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-2)] hover:text-[var(--text)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            All Writings
          </Link>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-[var(--surface-2)] text-[var(--text)] px-3 py-1.5 rounded-full">
              {article.category}
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--terracotta)]">
              <Clock className="h-3.5 w-3.5" /> {article.readTime}
            </span>
          </div>

          <h1 className="mt-5 font-serif text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[var(--text)]">
            {article.title}
          </h1>
          <p className="mt-4 font-sans text-sm text-[var(--text-2)]">
            By {article.author} · {article.date}
          </p>
          <p className="mt-5 font-hand text-2xl sm:text-3xl text-[var(--terracotta)] -rotate-1">
            {article.note}
          </p>
        </motion.div>

        <div className="mt-10 pb-6 space-y-6">
          {article.body.map((p, i) => (
            <p
              key={i}
              className={`font-sans text-[17px] leading-[1.95] text-[var(--text)] ${
                i === 0
                  ? "first-letter:font-serif first-letter:text-7xl first-letter:font-semibold first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.75] first-letter:text-[var(--terracotta)]"
                  : ""
              }`}
            >
              {p}
            </p>
          ))}
        </div>

        {article.tags && (
          <div className="mt-10 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)] mr-1">
              Keywords
            </span>
            {article.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border border-[var(--border-c)] bg-[var(--surface)] px-3 py-1.5 font-sans text-xs text-[var(--text-2)] hover:border-[var(--gold)] transition-colors"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="my-10 flex items-center gap-4">
          <span className="h-px flex-1 bg-[var(--border-c)]" />
          <span className="font-hand text-2xl text-[var(--gold)]">Devata.org</span>
          <span className="h-px flex-1 bg-[var(--border-c)]" />
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-24 lg:pb-32">
        <h2 className="font-serif text-2xl sm:text-3xl text-[var(--text)]">
          Continue reading
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-14">
          {related.map((a) => (
            <ArticleCardGrid key={a.id} article={a} />
          ))}
        </div>
      </section>
    </article>
  );
}
